import { useState, useCallback, useRef } from 'react';
import dashboardService from '../../api/dashboardService';
import type { DashboardLayoutItem, DashboardWidgetConfig, DashboardSettings, DashboardVisibility } from './widgets/types';
import { DashboardCtx, type DashboardState } from './dashboardCtx';
import { CANVAS_PADDING, MIN_WIDGET_GAP } from './gridConstants';

export const DashboardProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, setState] = useState<DashboardState>({
    dashboard: null,
    isEditing: false,
    isDirty: false,
    isSaving: false,
    isLoading: false,
    error: null,
    draftLayout: [],
    draftWidgets: [],
    draftSettings: {},
    draftVisibility: 'private',
    draftTitle: '',
    draftDescription: '',
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const loadDashboard = useCallback(async (id: string) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const res = await dashboardService.getDashboard(id);
      if (res.success && res.data) {
        setState((s) => ({
          ...s,
          dashboard: res.data!,
          draftLayout: res.data!.layout,
          draftWidgets: res.data!.widgets,
          draftSettings: res.data!.settings ?? {},
          draftVisibility: res.data!.visibility,
          draftTitle: res.data!.title,
          draftDescription: res.data!.description || '',
          isLoading: false,
          isEditing: false,
          isDirty: false,
        }));
      } else {
        setState((s) => ({
          ...s,
          isLoading: false,
          error: res.errorData?.message || 'Failed to load dashboard',
        }));
      }
    } catch (err) {
      setState((s) => ({
        ...s,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      }));
    }
  }, []);

  const enterEditMode = useCallback(() => {
    setState((s) => ({
      ...s,
      isEditing: true,
      isDirty: false,
    }));
  }, []);

  const cancelEdit = useCallback(() => {
    setState((s) => ({
      ...s,
      isEditing: false,
      isDirty: false,
      draftLayout: s.dashboard?.layout ?? [],
      draftWidgets: s.dashboard?.widgets ?? [],
      draftSettings: s.dashboard?.settings ?? {},
      draftVisibility: s.dashboard?.visibility ?? 'private',
      draftTitle: s.dashboard?.title ?? '',
      draftDescription: s.dashboard?.description || '',
    }));
  }, []);

  const saveDashboard = useCallback(async (): Promise<boolean> => {
    const s = stateRef.current;
    if (!s.dashboard) return false;

    setState((prev) => ({ ...prev, isSaving: true }));
    try {
      const res = await dashboardService.updateDashboard(s.dashboard.dashboard_id, {
        title: s.draftTitle,
        description: s.draftDescription || undefined,
        layout: s.draftLayout,
        widgets: s.draftWidgets,
        settings: s.draftSettings,
        visibility: s.draftVisibility,
      });

      if (res.success) {
        setState((prev) => ({
          ...prev,
          isSaving: false,
          isEditing: false,
          isDirty: false,
          dashboard: prev.dashboard
            ? { ...prev.dashboard, title: prev.draftTitle, description: prev.draftDescription || null, layout: prev.draftLayout, widgets: prev.draftWidgets, settings: prev.draftSettings, visibility: prev.draftVisibility }
            : null,
        }));
        return true;
      } else {
        setState((prev) => ({
          ...prev,
          isSaving: false,
          error: res.errorData?.message || 'Failed to save',
        }));
        return false;
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isSaving: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      }));
      return false;
    }
  }, []);

  const scheduleAutoSave = useCallback(() => {
    // No-op: auto-save disabled, all saves are explicit via the Save button
  }, []);

  const updateLayout = useCallback((layout: DashboardLayoutItem[]) => {
    setState((s) => ({ ...s, draftLayout: layout, isDirty: true }));
  }, []);

  const addWidget = useCallback(
    (widget: DashboardWidgetConfig, size: { width: number; height: number }) => {
      setState((s) => {
        const lowestBottom = s.draftLayout.reduce(
          (max, item) => Math.max(max, item.y + item.height),
          0,
        );
        const y = s.draftLayout.length === 0 ? CANVAS_PADDING : lowestBottom + MIN_WIDGET_GAP;
        const layoutItem: DashboardLayoutItem = {
          i: widget.id,
          x: CANVAS_PADDING,
          y,
          width: size.width,
          height: size.height,
        };
        return {
          ...s,
          draftWidgets: [...s.draftWidgets, widget],
          draftLayout: [...s.draftLayout, layoutItem],
          isDirty: true,
        };
      });
    },
    [],
  );

  const removeWidget = useCallback((widgetId: string) => {
    setState((s) => ({
      ...s,
      draftWidgets: s.draftWidgets.filter((w) => w.id !== widgetId),
      draftLayout: s.draftLayout.filter((l) => l.i !== widgetId),
      isDirty: true,
    }));
  }, []);

  const updateWidgetConfig = useCallback(
    (widgetId: string, config: Record<string, unknown>) => {
      setState((s) => ({
        ...s,
        draftWidgets: s.draftWidgets.map((w) =>
          w.id === widgetId ? { ...w, config: { ...w.config, ...config } } : w,
        ),
        isDirty: true,
      }));
    },
    [],
  );

  const updateWidgetTitle = useCallback(
    (widgetId: string, title: string) => {
      setState((s) => ({
        ...s,
        draftWidgets: s.draftWidgets.map((w) => (w.id === widgetId ? { ...w, title } : w)),
        isDirty: true,
      }));
    },
    [],
  );

  const updateWidgetShowTitle = useCallback(
    (widgetId: string, showTitle: boolean) => {
      setState((s) => ({
        ...s,
        draftWidgets: s.draftWidgets.map((w) => (w.id === widgetId ? { ...w, showTitle } : w)),
        isDirty: true,
      }));
    },
    [],
  );

  const updateSettings = useCallback(
    (patch: Partial<DashboardSettings>) => {
      setState((s) => ({
        ...s,
        draftSettings: { ...s.draftSettings, ...patch },
        isDirty: true,
      }));
    },
    [],
  );

  const updateVisibility = useCallback(
    (visibility: DashboardVisibility) => {
      setState((s) => ({ ...s, draftVisibility: visibility, isDirty: true }));
    },
    [],
  );

  const updateTitle = useCallback(
    (title: string) => {
      setState((s) => ({ ...s, draftTitle: title, isDirty: true }));
    },
    [],
  );

  const updateDescription = useCallback(
    (description: string) => {
      setState((s) => ({ ...s, draftDescription: description, isDirty: true }));
    },
    [],
  );

  const updateLayoutItem = useCallback(
    (
      widgetId: string,
      patch: Partial<Pick<DashboardLayoutItem, 'x' | 'y' | 'width' | 'height'>>,
    ) => {
      setState((s) => ({
        ...s,
        draftLayout: s.draftLayout.map((item) =>
          item.i === widgetId ? { ...item, ...patch } : item,
        ),
        isDirty: true,
      }));
    },
    [],
  );

  const value = {
    ...state,
    loadDashboard,
    enterEditMode,
    cancelEdit,
    saveDashboard,
    scheduleAutoSave,
    updateLayout,
    addWidget,
    removeWidget,
    updateWidgetConfig,
    updateWidgetTitle,
    updateWidgetShowTitle,
    updateLayoutItem,
    updateSettings,
    updateVisibility,
    updateTitle,
    updateDescription,
  };

  return <DashboardCtx.Provider value={value}>{children}</DashboardCtx.Provider>;
};
